<script lang="ts">
  import Sheet from './Sheet.svelte';
  import AppInnstillinger from './AppInnstillinger.svelte';

  type View =
    | { name: 'oversikt' }
    | { name: 'potte'; potteId: string }
    | { name: 'katalog' }
    | { name: 'dyrking' };
  let { active, onNavigate }: { active: View['name']; onNavigate: (v: View) => void } = $props();

  // Tannhjulet åpner innstillingene for appen (kalibrering, strømpris, logg ut).
  let visInnstillinger = $state(false);

  // «Kasser» dekker både oversikten og en åpnet blomsterkasse-detalj.
  const kasserAktiv = $derived(active === 'oversikt' || active === 'potte');
</script>

<!-- Appen tegnes under statuslinja på iPhone (black-translucent + viewport-fit=cover).
     Uten denne luften havnet tannhjulet under batterisymbolet og kunne ikke trykkes. -->
<header
  class="sticky top-0 z-30 bg-bg/85 backdrop-blur-md border-b border-border"
  style="padding-top: env(safe-area-inset-top);"
>
  <div class="mx-auto max-w-5xl px-4 sm:px-6 py-3 flex items-center gap-4">
    <button
      class="flex items-center gap-2 text-text hover:text-leaf transition-colors"
      onclick={() => onNavigate({ name: 'oversikt' })}
    >
      <span class="text-xl">🌱</span>
      <span class="font-display text-[17px] font-semibold tracking-tight">Plantepotte</span>
    </button>

    <!-- Desktop-nav -->
    <nav class="ml-auto hidden sm:flex items-center gap-1">
      <button
        class="px-3 py-1.5 rounded-lg text-sm transition-colors {kasserAktiv
          ? 'bg-surface-raised text-text'
          : 'text-text-muted hover:text-text hover:bg-surface-raised/50'}"
        onclick={() => onNavigate({ name: 'oversikt' })}
        aria-current={kasserAktiv ? 'page' : undefined}
      >
        Mine blomsterkasser
      </button>
      <button
        class="px-3 py-1.5 rounded-lg text-sm transition-colors {active === 'katalog'
          ? 'bg-surface-raised text-text'
          : 'text-text-muted hover:text-text hover:bg-surface-raised/50'}"
        onclick={() => onNavigate({ name: 'katalog' })}
        aria-current={active === 'katalog' ? 'page' : undefined}
      >
        Plantekatalog
      </button>
      <button
        class="px-3 py-1.5 rounded-lg text-sm transition-colors {active === 'dyrking'
          ? 'bg-surface-raised text-text'
          : 'text-text-muted hover:text-text hover:bg-surface-raised/50'}"
        onclick={() => onNavigate({ name: 'dyrking' })}
        aria-current={active === 'dyrking' ? 'page' : undefined}
      >
        Dyrking
      </button>
    </nav>

    <!-- Tannhjul: innstillinger for appen. Mobil har navigasjonen i bunnlinja. -->
    <button
      class="ml-auto sm:ml-2 btn-ghost !px-2 !py-2"
      onclick={() => (visInnstillinger = true)}
      aria-label="Innstillinger"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/></svg>
    </button>
  </div>
</header>

<!-- Mobil bunn-navigasjon (tommel-vennlig) -->
<nav
  class="sm:hidden fixed bottom-0 inset-x-0 z-30 bg-bg/95 backdrop-blur-md border-t border-border"
  style="padding-bottom: env(safe-area-inset-bottom);"
>
  <div class="flex">
    <button
      class="flex-1 flex flex-col items-center gap-0.5 py-2.5 transition-colors {kasserAktiv ? 'text-leaf' : 'text-text-muted'}"
      onclick={() => onNavigate({ name: 'oversikt' })}
      aria-current={kasserAktiv ? 'page' : undefined}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/></svg>
      <span class="text-[11px] font-medium">Kasser</span>
    </button>
    <button
      class="flex-1 flex flex-col items-center gap-0.5 py-2.5 transition-colors {active === 'katalog' ? 'text-leaf' : 'text-text-muted'}"
      onclick={() => onNavigate({ name: 'katalog' })}
      aria-current={active === 'katalog' ? 'page' : undefined}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
      <span class="text-[11px] font-medium">Katalog</span>
    </button>
    <button
      class="flex-1 flex flex-col items-center gap-0.5 py-2.5 transition-colors {active === 'dyrking' ? 'text-leaf' : 'text-text-muted'}"
      onclick={() => onNavigate({ name: 'dyrking' })}
      aria-current={active === 'dyrking' ? 'page' : undefined}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>
      <span class="text-[11px] font-medium">Dyrking</span>
    </button>
  </div>
</nav>

<Sheet open={visInnstillinger} onClose={() => (visInnstillinger = false)} label="Innstillinger">
  {#if visInnstillinger}
    <AppInnstillinger onLukk={() => (visInnstillinger = false)} />
  {/if}
</Sheet>
